import { Component, inject, input } from '@angular/core';
import { UiToastComponent } from '@design-system';
import { ActiveToast, ToastService } from '../../services/toast.service';

/** Which edge the stack is anchored to. */
export type ToastHostPosition = 'top' | 'bottom';

/**
 * Single toast host for kala-mobile. Belongs in `app.component.html` exactly once.
 *
 * `ui-toast` (`@design-system`) is a presentational banner with no host of its own, so the
 * positioning, the stacking order and the aria-live region all live here; the queue itself is
 * `ToastService`.
 *
 * Placement follows the shared mobile toast: aligned to the inline start (the right, since the app
 * is RTL) and sliding in horizontally. It differs in being anchored by the `position` input rather
 * than relocating on its own when the order form opens, and in inheriting `ui-toast`'s 430px Figma
 * max-width instead of the legacy 330px — so on a phone the banner is full-width either way.
 *
 * Note that this deliberately does not listen to `@shared`'s `NotificationService`. Any toast a
 * shared library raises through that service is therefore invisible in kala — so when wiring in a
 * lib that raises one (`@setting`'s effects, `@oms`, …), either migrate it or read whatever state
 * it also exposes and raise the toast here off that state — never by rendering the shared
 * notification itself.
 */
@Component({
	selector: 'app-toast-host',
	imports: [UiToastComponent],
	templateUrl: './toast-host.component.html',
	styleUrl: './toast-host.component.scss'
})
export class ToastHostComponent {
	/**
	 * `top` clears the `h-12` header; `bottom` clears the footer and keeps the toast in thumb reach.
	 * Set once on the host in `app.component.html` — this is a shell-level decision, not per-toast.
	 */
	readonly position = input<ToastHostPosition>('top');

	protected readonly toastService = inject(ToastService);

	/**
	 * A repeated message carries its tally as a `(2)` suffix, following the shared mobile toast,
	 * which rendered the same parenthesised count beside the text.
	 *
	 * It is appended to the string rather than rendered as its own element because `ui-toast` takes
	 * `message` as a plain input and projects no content. Growing the design-system component for
	 * this would put an un-designed badge in a Figma-derived banner every other app shares.
	 */
	protected messageWithCount(toast: ActiveToast): string {
		return toast.count > 1 ? `${toast.message} (${toast.count})` : toast.message;
	}

	/** The action control doubles as dismiss, so a tapped toast closes straight away. */
	protected onAction(toast: ActiveToast): void {
		toast.onAction?.();
		this.toastService.dismiss(toast.id);
	}
}
